// =============================================================================
// corporate-lines.ts — Schemas Zod públicos do diretório de linhas corporativas
// (F51-S18).
//
// Contexto: docs/planejamento-isolamento-de-inbox-e-privacidade-interna.md.
//
// O que é: a relação dos números do plano empresarial da instituição —
// financeiro, jurídico, diretoria, cada setor — que NÃO têm caixa de
// atendimento pareada. Sem esse diretório o sistema não tem como saber que os
// dois lados de uma conversa são internos, e a conversa entra na inbox como se
// fosse de um cidadão qualquer.
//
// LGPD (doc 17 §8.1): telefone de funcionário é dado pessoal. O contrato
// público NUNCA carrega o número em claro numa listagem:
//   - `phone_masked` (últimos 4 dígitos) é o que a lista devolve;
//   - o número completo só sai no detalhe (`GET /api/corporate-lines/:id`),
//     sob a mesma permissão `corporate_lines:manage`.
// O `phone_hash` (HMAC) NUNCA é exposto — é chave de cruzamento interna e
// devolvê-lo permitiria confirmar um número por força bruta offline.
// =============================================================================
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Domínios fechados
// ---------------------------------------------------------------------------
/**
 * Motivo estável pelo qual uma linha foi rejeitada na importação em lote.
 * Fechado de propósito: o relatório é lido por gestor e traduzido na UI, então
 * a string precisa ser estável entre versões.
 */
export const CorporateLineRejectionReasonSchema = z.enum([
    /** Depois de remover máscara e símbolos não sobrou dígito nenhum. */
    'empty',
    /** Sobrou algo que não é telefone (letras, poucos/muitos dígitos). */
    'invalid_format',
    /** O mesmo número apareceu duas vezes no próprio lote colado. */
    'duplicate_in_batch',
    /** O número já estava cadastrado na organização. */
    'already_registered',
]);
// ---------------------------------------------------------------------------
// Entrada — número bruto
// ---------------------------------------------------------------------------
/**
 * Telefone como o gestor digita ou cola: aceita máscara, espaço, parênteses,
 * hífen e `+`. A normalização (retirar símbolos, canonicalizar o nono dígito)
 * é do backend — validar formato rígido aqui só transferiria para o gestor um
 * trabalho que a máquina faz melhor, e a lista da operadora vem suja.
 */
export const corporateLinePhoneInputSchema = z
    .string()
    .trim()
    .min(1, 'Informe o número')
    .max(32, 'Número longo demais para ser um telefone')
    .describe('Telefone da linha corporativa, em qualquer formato: +55 69 99999-9999, 5569999999999');
const labelSchema = z
    .string()
    .trim()
    .min(1, 'Informe a quem pertence a linha')
    .max(120)
    .describe('A quem a linha pertence, como a instituição chama (ex.: "Financeiro — Marcos")');
const sectorSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .describe('Setor da instituição (ex.: "Financeiro", "Jurídico", "Diretoria")');
// ---------------------------------------------------------------------------
// Response
// ---------------------------------------------------------------------------
export const CorporateLineSchema = z.object({
    id: z.string().uuid(),
    organization_id: z.string().uuid(),
    label: z.string().describe('A quem a linha pertence'),
    sector: z.string().nullable().describe('Setor da instituição; null quando não informado'),
    user_id: z
        .string()
        .uuid()
        .nullable()
        .describe('Usuário do sistema dono da linha, quando a linha é de alguém que usa o Elemento'),
    phone_masked: z
        .string()
        .describe('Telefone mascarado — só os últimos 4 dígitos (ex.: "+55 69 ****-4321")'),
    is_active: z
        .boolean()
        .describe('Linha desativada continua no diretório (histórico) mas não cruza mais conversa'),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
/** Detalhe — único lugar do contrato público que devolve o número em claro. */
export const CorporateLineDetailSchema = CorporateLineSchema.extend({
    phone: z
        .string()
        .describe('Telefone completo, em dígitos puros (mesmo formato de contact_remote_id). ' +
        'Só no detalhe, nunca na listagem.'),
});
export const CorporateLineListResponseSchema = z.object({
    data: z.array(CorporateLineSchema),
    total: z.number().int().nonnegative().describe('Total de linhas que casam com o filtro'),
});
// ---------------------------------------------------------------------------
// Create / Update
// ---------------------------------------------------------------------------
export const corporateLineCreateSchema = z.object({
    phone: corporateLinePhoneInputSchema,
    label: labelSchema,
    sector: sectorSchema.nullish(),
    user_id: z.string().uuid().nullish().describe('Usuário do sistema dono da linha, se houver'),
});
/**
 * Atualização parcial. O telefone NÃO é editável: trocar o número é trocar a
 * identidade da linha e invalidaria silenciosamente qualquer cruzamento já
 * feito. Número errado se desativa e se cadastra de novo.
 */
export const corporateLineUpdateSchema = z
    .object({
    label: labelSchema.optional(),
    sector: sectorSchema.nullish(),
    user_id: z.string().uuid().nullish(),
    is_active: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
});
// ---------------------------------------------------------------------------
// Importação em lote
// ---------------------------------------------------------------------------
/**
 * Uma linha do lote. `raw` é a string exata que o gestor colou — devolvida
 * intacta no relatório para que ele reconheça a linha na planilha dele.
 */
export const corporateLineImportItemSchema = z.object({
    phone: corporateLinePhoneInputSchema,
    label: labelSchema.optional(),
    sector: sectorSchema.nullish(),
});
export const corporateLineImportSchema = z.object({
    items: z
        .array(corporateLineImportItemSchema)
        .min(1, 'Cole ao menos uma linha')
        .max(2000, 'Importe no máximo 2000 linhas por vez'),
    /** Rótulo aplicado às linhas do lote que não trouxeram rótulo próprio. */
    default_label: labelSchema.optional(),
});
export const CorporateLineImportAcceptedSchema = z.object({
    raw: z.string().describe('A string exata que veio na importação'),
    id: z.string().uuid(),
    phone_masked: z.string(),
    /**
     * `true` quando o número guardado difere do que foi colado (máscara
     * removida, nono dígito canonicalizado). Sem esse sinal o gestor acha que
     * cadastrou um número e cadastrou outro.
     */
    normalized: z.boolean(),
});
export const CorporateLineImportRejectedSchema = z.object({
    raw: z.string().describe('A string exata que veio na importação'),
    reason: CorporateLineRejectionReasonSchema,
});
export const CorporateLineImportResultSchema = z.object({
    accepted: z.array(CorporateLineImportAcceptedSchema),
    rejected: z.array(CorporateLineImportRejectedSchema),
    counts: z.object({
        received: z.number().int().nonnegative(),
        accepted: z.number().int().nonnegative(),
        normalized: z.number().int().nonnegative(),
        rejected: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=corporate-lines.js.map